import type { Product } from "../../products";
import { BUNDLE_CHECKOUT_URL, PRICING } from "@/lib/pricing-constants";
import { getProductScreenshots } from "@/lib/utils/get-product-screenshots";

export const redditScoutProProduct: Product = {
  id: "reddit-scout-pro",
  slug: "reddit-scout-pro",
  name: "Scout Pro for Reddit — Lead Mining With Intent Scoring",
  alternateName: "Scout Pro for Reddit",
  shortName: "Scout Pro for Reddit",
  url: "https://plugmonkey.xyz/product/reddit-scout-pro/",
  chromeStoreRedirect: "/redirect/reddit-scout-pro-chrome-store/",
  description:
    "Monitor Reddit for high-intent customer conversations. Keyword tracking + intent scoring (0-100) + lead management + CSV export. Turn Reddit threads into a lead pipeline. Free Chrome extension.",
  shortDescription:
    "Monitor Reddit for High-Intent Customer Conversations and Turn Them Into Leads",
  metaDescription:
    "Monitor Reddit for high-intent customer conversations: keyword tracking, 0-100 intent scoring, lead management, and CSV export. Free Chrome extension.",
  image: ["https://plugmonkey.xyz/product/reddit-scout-pro/og-image.png"],
  icon: {
    name: "FaRedditAlien",
    backgroundClasses: "bg-[#FF4500]",
    color: "#ffffff",
  },
  category: "Software > Browser Extensions > Social Media Monitoring",
  applicationCategory: "BrowserApplication",
  operatingSystem: "Chrome",
  audienceType: "SaaS Founders, Marketers, Sales Teams, Indie Hackers",
  freeOffer: {
    name: "Free Version",
    description:
      "Monitor up to 3 keywords across 3 subreddit groups with 25 visible matches, 25 saved leads, 3 CSV exports per week, and scans you start with Scan now (once an hour)",
    price: "0",
    priceCurrency: "USD",
    url: "https://plugmonkey.xyz/redirect/reddit-scout-pro-chrome-store/",
  },
  offers: {
    monthly: {
      name: "Monthly Subscription",
      description: "Pay as you go. Cancel anytime.",
      price: PRICING.MONTHLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/10f81b84-a54c-4adf-9fa1-f7cc0cf303bc",
    },
    yearly: {
      name: "Yearly Subscription",
      description: `Save ~${PRICING.YEARLY_SAVINGS_PERCENT}% vs monthly billing.`,
      price: PRICING.YEARLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/d3e65f35-4271-4c94-a2e6-94da49d73341",
    },
    lifetime: {
      name: "Lifetime License",
      description: "One-time payment. Own it forever.",
      price: PRICING.LIFETIME,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/fcf3c0f4-de45-459e-91c9-19668a9fdfaf",
      priceValidUntil: "2030-12-31",
      warranty: {
        durationOfWarranty: "P30D",
        warrantyScope: "30-day money-back guarantee",
      },
    },
  },
  bundleOffer: {
    name: "All-Access Bundle",
    description: "Get every extension we build, forever.",
    price: PRICING.BUNDLE_LIFETIME,
    priceCurrency: "USD",
    url: BUNDLE_CHECKOUT_URL,
  },
  aggregateRating: {
    ratingValue: "4.8",
    reviewCount: "0",
    bestRating: "5",
    worstRating: "1",
  },
  userCount: "3,120",
  keywords:
    "Reddit monitoring, Reddit lead generation, Reddit keyword tracking, social listening Reddit, Reddit intent scoring, Reddit scout, Reddit prospecting, Reddit marketing tool, Chrome extension Reddit",
  additionalProperties: [
    {
      name: "Platform",
      value: "Chrome Extension",
    },
    {
      name: "Version",
      value: "1.5.1",
    },
    {
      name: "License Type",
      value: "Lifetime",
    },
  ],
  version: "1.5.1",
  hero: {
    // Developer-controlled data with HTML spans for styling, same pattern as ProductPageLayout
    headline:
      "<span class='text-[#FF4500]'>Scout Pro for Reddit</span> — Turn Reddit Into a Lead Generation Machine",
    subheadline:
      "Monitor Reddit for high-intent customer conversations. Track keywords, score buying intent, manage leads, and export to CSV. Never miss a prospect again.",
    ctaText: "Install Free",
    badgeText: "v1.5.1 • Last updated Nov 4, 2026",
  },
  features: [
    {
      title: "Keyword Monitoring",
      description:
        "Set up keywords and scan Reddit with one click, or let PRO scan in the background on a schedule you choose. Get notified when someone mentions your product, problem, or niche in the subreddits you pick.",
      icon: "HiSearch",
    },
    {
      title: "Intent Scoring",
      description:
        "Every match gets a 0-100 intent score and one or more of 6 categories: Solution Request, Money Talk, Pain Point, Comparison, Recommendation, and General.",
      icon: "HiChartBar",
    },
    {
      title: "Lead Management",
      description:
        "Built-in CRM for Reddit leads. Star, archive, add notes, filter by intent score, and export everything to CSV for your sales pipeline.",
      icon: "HiUserGroup",
    },
  ],
  longFormFeatures: [
    {
      title: "Keyword Monitoring",
      description:
        "Set your keywords once and click Scan now any time. With PRO, Scout Pro for Reddit also scans automatically in the background. Get desktop notifications when a scan finds new high-intent matches. It paces its reads to stay under Reddit's limits.",
      bullets: [
        "Scan now on demand (free: once an hour)",
        "PRO: automatic background scans every 30 minutes, 1, 2 or 4 hours",
        "Desktop notifications for high-scoring matches after a scan",
        "One feed read covers every keyword for a subreddit",
      ],
    },
    {
      title: "6-Category Intent Scoring",
      description:
        "Every Reddit post and comment gets analyzed and scored from 0-100 from its intent category, how recent it is and how detailed it is. Focus your time on the highest-intent conversations instead of scrolling aimlessly.",
      bullets: [
        "Solution Request — asking for a tool or a way to do something",
        "Money Talk — talking about budget, price or paying",
        "Pain Point — frustrated with a current solution",
        "Comparison — evaluating options",
        "Recommendation — asking for suggestions",
        "General — a keyword match without a stronger buying signal",
      ],
    },
    {
      title: "Built-In Lead CRM",
      description:
        "Star promising leads, archive irrelevant ones, add private notes, and filter by intent score. It's a mini-CRM purpose-built for Reddit prospecting.",
      bullets: [
        "Star and archive leads",
        "Add private notes to any match",
        "Filter by intent score range",
        "Sort by date, score, or status",
      ],
    },
    {
      title: "CSV Export & Pipeline Integration",
      description:
        "Export your leads to CSV with all metadata: username, subreddit, intent score, post URL, your notes. Drop it straight into your CRM or outreach tool.",
      bullets: [
        "Full metadata CSV export",
        "Includes intent scores and categories",
        "Export filtered or full dataset",
        "Compatible with any CRM or spreadsheet",
      ],
    },
    {
      title: "On-Page Keyword Highlighting",
      description:
        "While browsing Reddit, your tracked keywords get highlighted directly on the page. Spot relevant conversations at a glance without searching.",
      bullets: [
        "Instant keyword highlighting as you browse Reddit",
        "Visual scanning for relevant posts",
        "Works on feeds, comments, and search results",
        "Toggle on/off per keyword",
      ],
    },
    {
      title: "Subreddit Groups",
      description:
        "Organize subreddits into groups for different campaigns or products. Monitor different keyword sets across different subreddit groups for targeted prospecting.",
      bullets: [
        "Group subreddits by campaign or product",
        "Different keywords per group",
        "Organized monitoring dashboard",
        "Quickly switch between campaigns",
      ],
    },
  ],
  faq: [
    {
      question: "How does Scout Pro for Reddit work?",
      answer:
        "Scout Pro for Reddit is a Chrome extension that monitors Reddit for conversations matching your keywords. Set up your keywords, choose subreddits to monitor, and the extension reads Reddit's public RSS feeds and matches your keywords on your device. Click Scan now any time; PRO also scans in the background on a schedule you choose. Every match gets an intent score from 0-100 so you can focus on the highest-quality leads. Manage everything from the extension's built-in dashboard.",
    },
    {
      question: "Is Scout Pro for Reddit safe to use?",
      answer:
        "Yes, Scout Pro for Reddit is completely safe. It works directly in your browser without requiring your Reddit credentials. No data is sent to our servers — all processing happens locally on your machine. We take privacy seriously and never sell or share your information.",
    },
    {
      question: "What is the difference between the Free and Pro versions?",
      answer:
        "The Free version lets you monitor up to 3 keywords in 3 subreddit groups, with 25 visible matches, 25 saved leads, and 3 CSV exports per week. Free scans run when you click Scan now (at most once an hour) or add a keyword. The Pro version offers unlimited keywords, groups, matches, leads and exports, plus automatic background scans every 30 minutes, 1, 2 or 4 hours (you choose in Settings), and priority support.",
    },
    {
      question: "Is the Pro license a subscription or a one-time payment?",
      answer:
        "You have options! We offer flexible Monthly and Yearly subscriptions, or you can choose the Lifetime License for a one-time payment to own it forever.",
    },
    {
      question: "How is the intent score calculated?",
      answer:
        "Each Reddit post or comment is analyzed across 6 intent categories: Solution Request, Money Talk, Pain Point, Comparison, Recommendation, and General. The overall score (0-100) comes from the category weight, a bonus for matching several categories, how recent the post is, whether the title asks a question, and how detailed the message is. Reddit's feeds carry no vote or comment counts, so the score does not use upvotes or engagement.",
    },
    {
      question: "Can I use Scout Pro for Reddit on multiple devices?",
      answer:
        "Yes! Your Pro license is tied to your account, so you can use Scout Pro for Reddit on any device where you have the extension installed. Simply install the extension on your other devices and activate the Pro features.",
    },
    {
      question: "What subreddits can I monitor?",
      answer:
        "You can monitor any public subreddit on Reddit. Create subreddit groups to organize your monitoring by product, campaign, or niche. Keywords that are not tied to a subreddit group are searched across Reddit over the last day.",
    },
    {
      question: "How do I export leads?",
      answer:
        "Click the Export button in the extension dashboard to download your leads as a CSV file. The export includes all metadata: username, subreddit, post URL, intent score, intent categories, your notes, and timestamps. Import directly into your CRM or spreadsheet tool.",
    },
    {
      question: "Can I get a refund if I'm not satisfied?",
      answer:
        "Yes, we offer a 30-day money-back guarantee. If Scout Pro for Reddit isn't working as described, contact our support team within 30 days of purchase — we'll troubleshoot first (most issues resolve in a single reply), and if we can't resolve it, you get a full refund.",
    },
    {
      question: "How does Scout Pro for Reddit compare to GummySearch?",
      answer:
        'GummySearch was a popular Reddit audience research tool, but it has shut down and is no longer available. Scout Pro for Reddit is actively maintained and offers keyword monitoring, intent scoring, lead management, and CSV export as a lightweight Chrome extension with no external dependencies. See the <a href="/comparison/reddit-scout-pro-vs-gummysearch/" style="color: var(--primary); text-decoration: underline;">full comparison with GummySearch</a> or browse the <a href="/best/best-reddit-monitoring-tools/" style="color: var(--primary); text-decoration: underline;">best Reddit monitoring tools</a>.',
    },
  ],
  relatedResources: [
    {
      category: "best",
      slug: "best-reddit-monitoring-tools",
      title: "Best Reddit Monitoring Tools 2026",
      description: "Scout Pro for Reddit ranked #1 — independent ranking of monitoring tools.",
    },
    {
      category: "guide",
      slug: "find-reddit-leads-saas",
      title: "How to Find SaaS Leads on Reddit",
      description: "Intent-scoring workflow for B2B SaaS founders + indie hackers.",
    },
    {
      category: "guide",
      slug: "monitor-reddit-brand-mentions",
      title: "Monitor Reddit Brand Mentions",
      description: "Set up keyword alerts to catch threads within hours, not days.",
    },
    {
      category: "faq",
      slug: "how-accurate-is-reddit-scout-intent-scoring",
      title: "How Accurate Is Scout Pro for Reddit's Intent Scoring?",
      description: "0-100 scale honest breakdown — what 70+ scores actually mean.",
    },
    {
      category: "faq",
      slug: "how-to-find-reddit-prospects-as-developer",
      title: "How to Find Reddit Prospects as a Developer",
      description: "Subreddit + keyword strategy for technical-product GTM.",
    },
    {
      category: "comparison",
      slug: "reddit-scout-pro-vs-mention",
      title: "Scout Pro for Reddit vs Mention",
      description: "Reddit-focused vs broad social listening — which fits your use case.",
    },
  ],
  testimonials: [
    {
      text: "Set up 6 keyword monitors across r/SaaS, r/startups, and r/smallbusiness. Got pinged on a thread where someone was asking for exactly the kind of tool I build, within a couple hours of it going up. Being early to the conversation is the whole value for me.",
      author: "Nate F.",
      role: "Solo founder, runs a proposal software tool",
      rating: 5,
    },
    {
      text: "The intent scoring is useful but not perfect — I'd say it's accurate about 80% of the time on high-intent flags. Still way better than manually scanning threads. I check the 70+ scores daily and ignore the rest.",
      author: "Ling W.",
      role: "Content marketer at a dev tools company",
      rating: 4,
    },
    {
      text: "We monitor Reddit for client mentions across 4 accounts. The CSV export drops straight into our CRM with intent scores attached. Replaced a janky Zapier + Google Alerts setup that missed half the mentions.",
      author: "Aisha B.",
      role: "Runs outbound at a B2B agency",
      rating: 5,
    },
  ],
  useCases: [
    {
      title: "SaaS Founders & Indie Hackers",
      description:
        "Monitor Reddit for people looking for tools in your niche. Find high-intent prospects complaining about competitors and reach out with your solution.",
    },
    {
      title: "Growth Marketers",
      description:
        "Track brand mentions, competitor discussions, and buying signals across Reddit. Build targeted outreach lists from genuine conversations.",
    },
    {
      title: "Sales Teams",
      description:
        "Identify warm leads from Reddit's massive user base. Export intent-scored leads to your CRM and prioritize outreach based on buying signals.",
    },
    {
      title: "Content Marketers",
      description:
        "Discover what problems your audience is talking about on Reddit. Use intent data to create content that addresses real pain points and drives conversions.",
    },
  ],
  ctaSection: {
    title: "Ready to Find Customers on Reddit?",
    subtitle:
      "Join founders and marketers who use Scout Pro for Reddit to turn Reddit conversations into paying customers",
  },
  productHunt: {
    slug: "reddit-scout-pro-by-plugmonkey",
    id: "reddit-scout-pro",
    title: "Scout Pro for Reddit",
    rating: "4.8",
    reviewCount: "0",
  },
  demoSection: {
    title: "See Scout Pro for Reddit in Action",
    description: "Watch how Scout Pro for Reddit finds high-intent leads automatically.",
    slides: getProductScreenshots("reddit-scout-pro"),
  },
  howTo: {
    name: "How to Use Scout Pro for Reddit to Find Leads",
    description:
      "Start monitoring Reddit for high-intent customer conversations in just a few simple steps",
    totalTime: "PT3M",
    tool: "Chrome Browser",
    steps: [
      {
        name: "Install the Extension",
        text: "Add Scout Pro for Reddit to Chrome with a single click. No registration required.",
        url: "https://plugmonkey.xyz/redirect/reddit-scout-pro-chrome-store/",
        position: 1,
      },
      {
        name: "Click the Floating Button on Reddit",
        text: "Navigate to reddit.com and click the floating Scout button in the bottom-right corner. This opens the dashboard where you can add keywords, choose subreddits, and organize them into groups.",
        position: 2,
      },
      {
        name: "Review Intent-Scored Matches",
        text: "Browse your matches sorted by intent score. Star the best leads, add notes, and archive irrelevant ones.",
        position: 3,
      },
      {
        name: "Export and Engage",
        text: "Export your highest-intent leads to CSV and reach out with helpful, relevant responses to turn conversations into customers.",
        position: 4,
      },
    ],
  },
  homepageCard: {
    backgroundIconName: "FaRedditAlien",
    backgroundIconOpacity: "opacity-5",
    backgroundIconTransform: "transform rotate-12",
    backgroundIconColor: "text-[#FF4500]",
    features: [
      { label: "Monitoring", value: "Scan Now / Auto (PRO)" },
      { label: "Scoring", value: "0-100 Intent" },
      { label: "Cost", value: "Free / Paid" },
    ],
  },
  displayOrder: 7,
  headerOrder: 500,
  pricing: {
    tagline: "Monitor Reddit for High-Intent Customer Conversations and Turn Them Into Leads",
    freeFeatures: [
      "3 keywords",
      "3 subreddit groups",
      "25 visible matches",
      "25 saved leads",
      "3 CSV exports per week",
      "Scan now (once an hour)",
      "Full intent scoring (6 categories)",
      "On-page keyword highlighting",
    ],
    proFeatures: [
      "Unlimited keywords",
      "Unlimited subreddit groups",
      "Unlimited matches and leads",
      "Unlimited CSV exports",
      "Automatic background scans every 30 minutes to 4 hours",
      "Priority customer support",
      "All future updates included",
    ],
    useCase: "Perfect for SaaS founders and marketers finding leads on Reddit",
  },
  flowData: {
    installation: {
      headline: "You're ready to find leads on Reddit.",
      subheadline: "Here is how to get the most out of Scout Pro for Reddit in 60 seconds.",
      guideSteps: [
        {
          title: "Pin the Extension",
          description:
            "Click the puzzle piece icon in your Chrome toolbar and pin Scout Pro for Reddit for easy access.",
          image: "pin-extension",
        },
        {
          title: "Open the Dashboard on Reddit",
          description:
            "Visit any Reddit page and click the floating Scout Pro for Reddit button to open the dashboard. Add keywords related to your product or niche, and choose subreddits to scan.",
          image: "add-keywords",
          screenshot: "screenshot-1.png",
        },
        {
          title: "Review Matches & Export Leads",
          description:
            "Click Scan now (PRO also scans in the background on a schedule you choose) and get a desktop notification when a scan finds high-scoring matches. Review intent-scored leads, star the best, and export to CSV.",
          image: "start-monitoring",
          screenshot: "screenshot-1.png",
        },
      ],
      primaryCtaText: "Open Reddit and Start Monitoring",
      primaryCtaUrl: "https://www.reddit.com",
      emailCapture: {
        headline: "Get weekly Reddit growth tactics",
        description:
          "Join founders and marketers receiving weekly tips on Reddit lead generation and social selling. No fluff, just tactics.",
        buttonText: "Send me the tactics",
      },
    },
    uninstallation: {
      headline: "Sorry to see you go.",
      subheadline: "Feedback is a gift. Tell us how we can improve.",
      survey: {
        question: "What was the main reason for uninstalling?",
        options: [
          "It didn't work as expected",
          "I found a better alternative",
          "It's too expensive",
          "I don't need it anymore",
          "Other",
        ],
      },
    },
  },
};
